<template>
  <div
    v-loading="isInitializing"
    class="max-w-360 m-auto w-full h-full flex flex-col justify-center items-center"
  >
    <div class="flex-1 flex justify-center items-center">
      <KeyboardPanel
        :mode="currentMode"
        :show-keyboard-actions="showKeyboardActions"
        :key-selectable="keySelectable"
        :key-travel-map="keyTravelMap"
        @selection-change="handleSelectionChange"
      />
    </div>

    <div class="flex flex-col w-full gap-4.5 h-82 shrink-0 mb-2">
      <div class="h-10" />
      <div
        v-if="currentMode === 'demo'"
        class="w-full h-67.5 min-h-fit overflow-x-auto flex items-start justify-center gap-2"
      >
        <ArcGaugeSlider
          :real-time-travel="realTimeTravel"
          :has-crossed-absolute-trigger="hasCrossedAbsoluteTrigger"
          @update:mode="handleChangeMode"
        />

        <div class="flex gap-2 glass-frosted relative rounded-8xl w-250 min-h-67.5 p-4">
          <ItineraryTrigger
            :absolute-trigger="editingState.absoluteTrigger"
            :rt-trigger-enable="editingState.rtTriggerEnable"
            :rt-switch-disabled="rtSwitchDisabled"
            :can-edit-trigger="hasSelectedKeys || isPresetMode"
            :trigger-slider-disabled="triggerSliderDisabled"
            @update:absolute-trigger="handleAbsoluteTriggerChange"
            @update:rt-trigger-enable="handleRtTriggerEnableChange"
          />

          <BottomDeadZone
            :bottom-dead-zone="editingState.bottomDeadZone"
            :dead-zone-enabled="deadZoneEnabled"
            @update:bottom-dead-zone="handleBottomDeadZoneChange"
          />

          <RTSensitivityTrigger
            v-model:is-related="isRelated"
            :rt-trigger-sensitivity="editingState.rtTriggerSensitivity"
            :rt-reset-sensitivity="editingState.rtResetSensitivity"
            :sensitivity-enabled="sensitivityEnabled"
            :related-toggle-enabled="relatedToggleEnabled"
            :rt-switch-index="rtSwitchIndex"
            :preset-labels="presetLabels"
            @update:rt-trigger-sensitivity="handleRtTriggerSensitivityChange"
            @update:rt-reset-sensitivity="handleRtResetSensitivityChange"
            @switch-rt="switchRT"
          />
        </div>
      </div>

      <div v-if="currentMode === 'calibration'" class="w-full h-67.5 overflow-x-auto">
        <CalibrationPanel @update:mode="handleChangeMode" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { computed, ref, watch } from 'vue'
  import { storeToRefs } from 'pinia'
  import KeyboardPanel from './components/KeyboardPanel.vue'
  import ArcGaugeSlider from './components/ArcGaugeSlider.vue'
  import ItineraryTrigger from './components/ItineraryTrigger.vue'
  import BottomDeadZone from './components/BottomDeadZone.vue'
  import RTSensitivityTrigger from './components/RTSensitivityTrigger.vue'
  import CalibrationPanel from './components/CalibrationPanel.vue'
  import { useDeviceStore } from '@/stores/device'
  import { useItineraryModel } from './composables/useItineraryModel'
  import { useItineraryDevice } from './composables/useItineraryDevice'
  import { useRTReport } from './composables/useRTReport'
  import { useDemoMode } from './composables/useDemoMode'

  const model = useItineraryModel()
  const {
    keyTravelMap,
    editingState,
    isRelated,
    rtSwitchIndex,
    hasSelectedKeys,
    isPresetMode,
    rtSwitchDisabled,
    triggerSliderDisabled,
    deadZoneEnabled,
    sensitivityEnabled,
    relatedToggleEnabled,
    showKeyboardActions,
    keySelectable,
    presetLabels,
    getKeyTravel,
    setSelectedKeys,
    initAllKeys,
    syncFieldToAllKeys,
  } = model

  const { isInitializing, switchRT, applyReportedSwitch, commitTravel } = useItineraryDevice(model)

  const currentMode = ref<'calibration' | 'demo'>('demo')
  const isDemoMode = computed(() => currentMode.value === 'demo')

  const { realTimeTravel, hasCrossedAbsoluteTrigger } = useDemoMode({
    getKeyTravel,
    enabled: isDemoMode,
  })

  // 设备上报 RT 切换：在外层统一处理，不写回设备
  useRTReport((data) => {
    applyReportedSwitch(data.rt)
  })

  // 布局就绪后初始化按键行程 map（原 KeyboardPanel 职责上收）
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)
  watch(
    () => currentDevice.value?.manifest.layout,
    (layout) => {
      if (!layout) return
      const indices = layout.keys.flatMap((row) => row.map((key) => key.key))
      initAllKeys(indices)
    },
    { immediate: true },
  )

  const handleSelectionChange = (keys: { index: number }[]) => {
    setSelectedKeys(keys)
  }

  const handleChangeMode = (mode: 'calibration' | 'demo') => {
    currentMode.value = mode
  }

  const handleAbsoluteTriggerChange = (value: number) => {
    editingState.absoluteTrigger = value
    if (isPresetMode.value) {
      syncFieldToAllKeys('absoluteTrigger', value)
    }
    commitTravel()
  }

  const handleRtTriggerEnableChange = (value: boolean) => {
    editingState.rtTriggerEnable = value
    commitTravel()
  }

  const handleBottomDeadZoneChange = (value: number) => {
    editingState.bottomDeadZone = value
    commitTravel()
  }

  const handleRtTriggerSensitivityChange = (value: number) => {
    editingState.rtTriggerSensitivity = value
    commitTravel()
  }

  const handleRtResetSensitivityChange = (value: number) => {
    editingState.rtResetSensitivity = value
    commitTravel()
  }
</script>

<style lang="scss" scoped></style>
